import * as base64Buffer from "base64-arraybuffer";
import { getUint32 } from "@/utils/ByteUtils";
import { compareHyDryEventMessages } from "../repository/HyDryLogsRepository";
import { getComBoxErrorFlagDescriptions } from "./ComBox";
import { IresDevice, isInWarning } from "./IresDevice";

/**
 * Enumeration for different categories of HyDry events.
 */
export enum HyDryEventCategory {
  ORGANISATION = 1,
  DRYING_PROGRESS = 2,
  ALERT = 3,
  OTHER = 4,
  IRES = 5,

  NOTE = 100,
  LID = 101,
  PAUSE = 102,
  SENSOR = 103,

  NOT_IN_MESSAGE = 255,
}

export enum HyDryEvent {
  POWERED_ON,
  POWERED_OFF,
  POWERED_OFF_WHILE_DRYING,

  NEW_DRYING,

  TURNED_ON,
  TURNED_OFF,

  DRYING_PARAMETERS_CHANGED,

  DRYING_STARTED,
  DRYING_RESUMED,
  DRYING_SUSPENDED,
  DRYING_ENDED,

  LID_OPENED,
  LID_CLOSED,

  MANUAL_PAUSE_STARTED,
  MANUAL_PAUSE_ENDED,

  PROGRAM_PAUSE_STARTED,
  PROGRAM_PAUSE_ENDED,

  TIMER_PAUSE_STARTED,
  TIMER_PAUSE_ENDED,

  //Gerät: Trocknungsverlauf nicht plausibel
  DRYING_PROGRESS_NOT_PLAUSIBLE,

  //Gerät: Trocknungsbeginn nicht plausibel
  DRYING_START_NOT_PLAUSIBLE,

  //Gerät: Trocknungsfortschritt prüfen
  CHECK_DRYING_PROGRESS,

  //Gerät: Qualitätsbeurteilung Trocknung
  DRYING_QUALITY_ASSESSMENT,

  //Gerät: Stromverlust innerhalb Trocknung (211 → 111)
  POWER_FAIL_WHILE_DRYING,

  //Gerät: Lägere Stromverlust innerhalb Trocknung
  PROLONGED_POWER_FAIL_WHILE_DRYING,

  MALFUNCTION,
  MALFUNCTION_WHILE_DRYING,

  WARNING,
  WARNING_WHILE_DRYING,

  //Gerät: IoT überfällig (2)
  DEVICE_OVERDUE,

  PROGRAM_PHASE_ENDED,

  DRYING_PROGRAM_STARTED,
  DRYING_PROGRAM_DONE,
  DRYING_PROGRAM_TERMINATED,

  UNKNOWN,

  NOTE,

  ROOM_DRYING_ROOM_TEMPERATURE_VERY_LOW,
  ROOM_DRYING_ROOM_HUMIDITY_VERY_HIGH,

  ROOM_DRYING_PAUSE_STARTED_TEMPERATURE_TOO_HIGH,
  ROOM_DRYING_PAUSE_ENDED_TEMPERATURE_TOO_HIGH,

  ROOM_DRYING_PAUSE_STARTED_HUMIDITY_TARGET_REACHED,
  ROOM_DRYING_PAUSE_ENDED_HUMIDITY_TARGET_REACHED,

  SENSOR_CONFIGURATION_CHANGED
}

export enum HyDryPanelEvent {
  POWER_UP,
  POWER_FAIL,

  MANUAL_START,
  PROGRAM_START_DELAY,

  PROGRAM_START_FOLLOW_UP,
  PROGRAM_END_LAST_STEP,
  PROGRAM_END_MAX_DURATION,

  PROGRAM_END_HEAT_RATIO,
  MANUAL_STOP,
  BREAK_COOL,
  PROGRAM_CHAIN_MIN_DURATION,
  PROGRAM_CHAIN_MAX_DURATION,

  STEP,
  RESTART,
  MAX_HEAT_DURATION,
  CHANGE_OF_POSITION,
  MAX_SOIL_TEMP_REACHED,

  WALL_OVER_TEMP,
  PCB_OVER_TEMP,
  MAIN_CHIP_OVER_TEMP,
  EDGE_CHIP_OVER_TEMP,
  MAIN_PANEL_RE_ENABLED,
  EDGE_PANEL_RE_ENABLED,

  NB1_NO_NET,

  EEPROM_MALFUNCTION,
  INVALID_CONFIG,
  SPI_FLASH_MALFUNCTION,
  DATALOG_CORRUPTED,
  RTCC_MALFUNCTION,
  RTCC_INVALID_TIME,
  ADC_MALFUNCTION,
  IR_SENSOR_MALFUNCTION,

  EM_MALFUNCTION,
  SSR_MALFUNCTION,
  INVALID_PROGRAMS,

  ACC_MALFUNCTION,
  BLE_MALFUNCTION,
  NB1_MALFUNCTION,

  FW_UPDATE_APPLY,
  FW_UPDATE_PERFORMED,
  
  POOR,
  PIR,
  POWER_THROTTLE_STEP,
  SUPPLY_VOLTAGE_FLUCTUATION,
  SUPPLY_VOLTAGE_STABLE,
  NB1_POWER_FAIL,

  CFG_REBOOT,
  FW_REBOOT,
  UTC_REBOOT,

  START_OF_DEPASSIVATION,
  END_OF_DEPASSIVATION,

  NOTE,

  UNKNOWN
}

export interface UnreadHyDryEventMessagesInfo {
  eventCategory: HyDryEventCategory;
  latestMessage: HyDryEventMessage;
  numUnread: number;
}

export interface LatestHyDryEventMessageInfo {
  serialNumber: string;
  unreadMessages: UnreadHyDryEventMessagesInfo[];
}

export interface HyDryEventMessageCollection {
  latestHyDryEventMessages: LatestHyDryEventMessageInfo[];
}

export interface HyDryEventMessage {
  serialNumber: string;
  eventCategory: HyDryEventCategory;
  event: HyDryEvent;
  eventNumber: number;
  messageDate: Date;
  wasRead: boolean;
  additionalData?: string;
}

export interface HyDryEventsPage {
  /* eslint-disable-next-line */
  comBoxes: any;
  /* eslint-disable-next-line */
  panels: any;
}

export function getHyDryEventDescriptionStringRes(
  hyDryEvent: HyDryEvent
): string {
  switch (hyDryEvent) {
    case HyDryEvent.ROOM_DRYING_ROOM_TEMPERATURE_VERY_LOW:
      return "roomDryingTemperatureVeryLow";
    case HyDryEvent.ROOM_DRYING_ROOM_HUMIDITY_VERY_HIGH:
      return "roomDryingHumidityVeryHigh";

    case HyDryEvent.ROOM_DRYING_PAUSE_STARTED_TEMPERATURE_TOO_HIGH:
      return "roomDryingPauseStartedTemperatureTooHigh";
    case HyDryEvent.ROOM_DRYING_PAUSE_ENDED_TEMPERATURE_TOO_HIGH:
      return "roomDryingPauseEndendTemperatureTooHigh";

    case HyDryEvent.ROOM_DRYING_PAUSE_STARTED_HUMIDITY_TARGET_REACHED:
      return "roomDryingPauseStartedHumidityTargetReached";
    case HyDryEvent.ROOM_DRYING_PAUSE_ENDED_HUMIDITY_TARGET_REACHED:
      return "roomDryingPauseEndendHumidityTargetReached";

    case HyDryEvent.PROGRAM_PHASE_ENDED:
      return "programPhaseEnded";

    case HyDryEvent.NOTE:
      return "note";

    case HyDryEvent.POWERED_ON:
      return "poweredOn";
    case HyDryEvent.POWERED_OFF:
      return "poweredOff";
    case HyDryEvent.POWERED_OFF_WHILE_DRYING:
      return "poweredOffWhileDrying";
    case HyDryEvent.DRYING_PARAMETERS_CHANGED:
      return "dryingParametersChanged";

    case HyDryEvent.DRYING_PROGRAM_STARTED:
      return "dryingProgramStarted";
    case HyDryEvent.DRYING_PROGRAM_DONE:
      return "dryingProgramDone";
    case HyDryEvent.DRYING_PROGRAM_TERMINATED:
      return "dryingProgramTerminated";

    case HyDryEvent.TURNED_ON:
      return "turnedOn";
    case HyDryEvent.TURNED_OFF:
      return "turnedOff";
    case HyDryEvent.NEW_DRYING:
      return "newDrying";

    case HyDryEvent.DRYING_STARTED:
      return "dryingStarted";
    case HyDryEvent.DRYING_SUSPENDED:
      return "dryingSuspended";
    case HyDryEvent.DRYING_RESUMED:
      return "dryingResumed";
    case HyDryEvent.DRYING_ENDED:
      return "dryingEnded";

    case HyDryEvent.LID_OPENED:
      return "lidOpened";
    case HyDryEvent.LID_CLOSED:
      return "lidClosed";

    case HyDryEvent.MANUAL_PAUSE_STARTED:
      return "manualPauseStarted";
    case HyDryEvent.MANUAL_PAUSE_ENDED:
      return "manualPauseEnded";

    case HyDryEvent.TIMER_PAUSE_STARTED:
      return "timerPauseStarted";
    case HyDryEvent.TIMER_PAUSE_ENDED:
      return "timerPauseEnded";

    case HyDryEvent.PROGRAM_PAUSE_STARTED:
      return "programPauseStarted";
    case HyDryEvent.PROGRAM_PAUSE_ENDED:
      return "programPauseEnded";

    case HyDryEvent.DRYING_PROGRESS_NOT_PLAUSIBLE:
      return "dryingProgressNotPlausible";
    case HyDryEvent.DRYING_START_NOT_PLAUSIBLE:
      return "dryingStartNotPlausible";
    case HyDryEvent.CHECK_DRYING_PROGRESS:
      return "checkDryingProgress";
    case HyDryEvent.DRYING_QUALITY_ASSESSMENT:
      return "dryingQualityAssessment";
    case HyDryEvent.POWER_FAIL_WHILE_DRYING:
      return "powerFailWhileDrying";
    case HyDryEvent.PROLONGED_POWER_FAIL_WHILE_DRYING:
      return "powerFailWhileDrying";
    case HyDryEvent.DEVICE_OVERDUE:
      return "deviceOverdue";
    case HyDryEvent.MALFUNCTION:
      return "malfunction";
    case HyDryEvent.WARNING:
      return "warning";
    case HyDryEvent.MALFUNCTION_WHILE_DRYING:
      return "malfunctionWhileDrying";
    case HyDryEvent.WARNING_WHILE_DRYING:
      return "warningWhileDrying";

    case HyDryEvent.SENSOR_CONFIGURATION_CHANGED:
      return "sensorConfigurationChanged";

    case HyDryEvent.UNKNOWN:
      return "unknownEvent";
  }
}

export function getHyDryEventNumber(hyDryEvent: HyDryEvent): number {
  switch (hyDryEvent) {
    case HyDryEvent.POWERED_ON:
      return 0;
    case HyDryEvent.POWERED_OFF:
      return 1;
    case HyDryEvent.NEW_DRYING:
      return 2;
    case HyDryEvent.TURNED_ON:
      return 3;
    case HyDryEvent.TURNED_OFF:
      return 4;
    case HyDryEvent.POWERED_OFF_WHILE_DRYING:
      return 5;
    case HyDryEvent.DRYING_PARAMETERS_CHANGED:
      return 6;

    case HyDryEvent.DRYING_STARTED:
      return 30;
    case HyDryEvent.DRYING_RESUMED:
      return 31;
    case HyDryEvent.DRYING_SUSPENDED:
      return 32;
    case HyDryEvent.DRYING_ENDED:
      return 33;
    case HyDryEvent.POWER_FAIL_WHILE_DRYING:
      return 34;
    case HyDryEvent.PROLONGED_POWER_FAIL_WHILE_DRYING:
      return 35;
    case HyDryEvent.PROGRAM_PHASE_ENDED:
      return 36;
    case HyDryEvent.DRYING_PROGRAM_STARTED:
      return 37;
    case HyDryEvent.DRYING_PROGRAM_DONE:
      return 38;
    case HyDryEvent.DRYING_PROGRAM_TERMINATED:
      return 39;

    case HyDryEvent.LID_CLOSED:
      return 60;
    case HyDryEvent.LID_OPENED:
      return 61;
    case HyDryEvent.MANUAL_PAUSE_STARTED:
      return 62;
    case HyDryEvent.MANUAL_PAUSE_ENDED:
      return 63;
    case HyDryEvent.PROGRAM_PAUSE_STARTED:
      return 64;
    case HyDryEvent.PROGRAM_PAUSE_ENDED:
      return 65;
    case HyDryEvent.TIMER_PAUSE_STARTED:
      return 66;
    case HyDryEvent.TIMER_PAUSE_ENDED:
      return 67;

    case HyDryEvent.SENSOR_CONFIGURATION_CHANGED:
      return 77;

    case HyDryEvent.ROOM_DRYING_ROOM_TEMPERATURE_VERY_LOW:
      return 80;
    case HyDryEvent.ROOM_DRYING_ROOM_HUMIDITY_VERY_HIGH:
      return 81;
    case HyDryEvent.ROOM_DRYING_PAUSE_STARTED_TEMPERATURE_TOO_HIGH:
      return 82;
    case HyDryEvent.ROOM_DRYING_PAUSE_ENDED_TEMPERATURE_TOO_HIGH:
      return 83;
    case HyDryEvent.ROOM_DRYING_PAUSE_STARTED_HUMIDITY_TARGET_REACHED:
      return 84;
    case HyDryEvent.ROOM_DRYING_PAUSE_ENDED_HUMIDITY_TARGET_REACHED:
      return 85;

    case HyDryEvent.DRYING_PROGRESS_NOT_PLAUSIBLE:
      return 90;
    case HyDryEvent.DRYING_START_NOT_PLAUSIBLE:
      return 91;
    case HyDryEvent.CHECK_DRYING_PROGRESS:
      return 92;
    case HyDryEvent.DRYING_QUALITY_ASSESSMENT:
      return 93;
    case HyDryEvent.DEVICE_OVERDUE:
      return 95;
    case HyDryEvent.MALFUNCTION:
      return 96;
    case HyDryEvent.MALFUNCTION_WHILE_DRYING:
      return 97;
    case HyDryEvent.WARNING:
      return 98;
    case HyDryEvent.WARNING_WHILE_DRYING:
      return 99;

    case HyDryEvent.UNKNOWN:
      return 256;

    case HyDryEvent.NOTE:
      return 1000;
  }
}

const WARNING_OR_ERROR_EVENTS = new Set([
  getHyDryEventNumber(HyDryEvent.WARNING),
  getHyDryEventNumber(HyDryEvent.WARNING_WHILE_DRYING),
  getHyDryEventNumber(HyDryEvent.MALFUNCTION),
  getHyDryEventNumber(HyDryEvent.MALFUNCTION_WHILE_DRYING)
]);

export function isEventNumberOfWarningOrError(eventNumber: number) {
  return WARNING_OR_ERROR_EVENTS.has(eventNumber);
}

export function getHyDryEventFromEventNumber(eventNumber: number): HyDryEvent {
  switch (eventNumber) {
    case 0:
      return HyDryEvent.POWERED_ON;
    case 1:
      return HyDryEvent.POWERED_OFF;
    case 2:
      return HyDryEvent.NEW_DRYING;
    case 3:
      return HyDryEvent.TURNED_ON;
    case 4:
      return HyDryEvent.TURNED_OFF;
    case 5:
      return HyDryEvent.POWERED_OFF_WHILE_DRYING;
    case 6:
      return HyDryEvent.DRYING_PARAMETERS_CHANGED;

    case 30:
      return HyDryEvent.DRYING_STARTED;
    case 31:
      return HyDryEvent.DRYING_RESUMED;
    case 32:
      return HyDryEvent.DRYING_SUSPENDED;
    case 33:
      return HyDryEvent.DRYING_ENDED;
    case 34:
      return HyDryEvent.POWER_FAIL_WHILE_DRYING;
    case 35:
      return HyDryEvent.PROLONGED_POWER_FAIL_WHILE_DRYING;
    case 36:
      return HyDryEvent.PROGRAM_PHASE_ENDED;
    case 37:
      return HyDryEvent.DRYING_PROGRAM_STARTED;
    case 38:
      return HyDryEvent.DRYING_PROGRAM_DONE;
    case 39:
      return HyDryEvent.DRYING_PROGRAM_TERMINATED;

    case 60:
      return HyDryEvent.LID_CLOSED;
    case 61:
      return HyDryEvent.LID_OPENED;
    case 62:
      return HyDryEvent.MANUAL_PAUSE_STARTED;
    case 63:
      return HyDryEvent.MANUAL_PAUSE_ENDED;
    case 64:
      return HyDryEvent.PROGRAM_PAUSE_STARTED;
    case 65:
      return HyDryEvent.PROGRAM_PAUSE_ENDED;
    case 66:
      return HyDryEvent.TIMER_PAUSE_STARTED;
    case 67:
      return HyDryEvent.TIMER_PAUSE_ENDED;

    // ==================== FIRMWARE v4.0.0+ CHANGES ====================
    case 77:
      return HyDryEvent.SENSOR_CONFIGURATION_CHANGED;

    case 80:
      return HyDryEvent.ROOM_DRYING_ROOM_TEMPERATURE_VERY_LOW;
    case 81:
      return HyDryEvent.ROOM_DRYING_ROOM_HUMIDITY_VERY_HIGH;
    case 82:
      return HyDryEvent.ROOM_DRYING_PAUSE_STARTED_TEMPERATURE_TOO_HIGH;
    case 83:
      return HyDryEvent.ROOM_DRYING_PAUSE_ENDED_TEMPERATURE_TOO_HIGH;
    case 84:
      return HyDryEvent.ROOM_DRYING_PAUSE_STARTED_HUMIDITY_TARGET_REACHED;
    case 85:
      return HyDryEvent.ROOM_DRYING_PAUSE_ENDED_HUMIDITY_TARGET_REACHED;

    case 90:
      return HyDryEvent.DRYING_PROGRESS_NOT_PLAUSIBLE;
    case 91:
      return HyDryEvent.DRYING_START_NOT_PLAUSIBLE;
    case 92:
      return HyDryEvent.CHECK_DRYING_PROGRESS;
    case 93:
      return HyDryEvent.DRYING_QUALITY_ASSESSMENT;
    case 95:
      return HyDryEvent.DEVICE_OVERDUE;
    case 96:
      return HyDryEvent.MALFUNCTION;
    case 97:
      return HyDryEvent.MALFUNCTION_WHILE_DRYING;
    case 98:
      return HyDryEvent.WARNING;
    case 99:
      return HyDryEvent.WARNING_WHILE_DRYING;

    case 1000:
      return HyDryEvent.NOTE;
  }

  return HyDryEvent.UNKNOWN;
}

export function getHyDryPanelEventNumber(
  hyDryPanelEvent: HyDryPanelEvent
): number {
  switch (hyDryPanelEvent) {
    case HyDryPanelEvent.POWER_UP:
      return 1;
    case HyDryPanelEvent.POWER_FAIL:
      return 2;

    case HyDryPanelEvent.MANUAL_START:
      return 5;
    case HyDryPanelEvent.PROGRAM_START_DELAY:
      return 6;

    case HyDryPanelEvent.PROGRAM_START_FOLLOW_UP:
      return 9;
    case HyDryPanelEvent.PROGRAM_END_LAST_STEP:
      return 10;
    case HyDryPanelEvent.PROGRAM_END_MAX_DURATION:
      return 11;
    case HyDryPanelEvent.PROGRAM_END_HEAT_RATIO:
      return 12;
    case HyDryPanelEvent.MANUAL_STOP:
      return 13;
    case HyDryPanelEvent.BREAK_COOL:
      return 14;
    case HyDryPanelEvent.PROGRAM_CHAIN_MIN_DURATION:
      return 15;
    case HyDryPanelEvent.PROGRAM_CHAIN_MAX_DURATION:
      return 16;

    case HyDryPanelEvent.STEP:
      return 18;
    case HyDryPanelEvent.RESTART:
      return 19;
    case HyDryPanelEvent.MAX_HEAT_DURATION:
      return 20;
    case HyDryPanelEvent.CHANGE_OF_POSITION:
      return 21;
    case HyDryPanelEvent.MAX_SOIL_TEMP_REACHED:
      return 22;

    case HyDryPanelEvent.WALL_OVER_TEMP:
      return 24;
    case HyDryPanelEvent.PCB_OVER_TEMP:
      return 25;
    case HyDryPanelEvent.MAIN_CHIP_OVER_TEMP:
      return 26;
    case HyDryPanelEvent.EDGE_CHIP_OVER_TEMP:
      return 27;
    case HyDryPanelEvent.MAIN_PANEL_RE_ENABLED:
      return 28;
    case HyDryPanelEvent.EDGE_PANEL_RE_ENABLED:
      return 29;

    case HyDryPanelEvent.NB1_NO_NET:
      return 39;

    case HyDryPanelEvent.EEPROM_MALFUNCTION:
      return 50;
    case HyDryPanelEvent.INVALID_CONFIG:
      return 51;
    case HyDryPanelEvent.SPI_FLASH_MALFUNCTION:
      return 52;
    case HyDryPanelEvent.DATALOG_CORRUPTED:
      return 53;
    case HyDryPanelEvent.RTCC_MALFUNCTION:
      return 54;
    case HyDryPanelEvent.RTCC_INVALID_TIME:
      return 55;
    case HyDryPanelEvent.ADC_MALFUNCTION:
      return 56;
    case HyDryPanelEvent.IR_SENSOR_MALFUNCTION:
      return 57;

    case HyDryPanelEvent.EM_MALFUNCTION:
      return 59;
    case HyDryPanelEvent.SSR_MALFUNCTION:
      return 60;
    case HyDryPanelEvent.INVALID_PROGRAMS:
      return 61;

    case HyDryPanelEvent.ACC_MALFUNCTION:
      return 63;
    case HyDryPanelEvent.BLE_MALFUNCTION:
      return 64;
    case HyDryPanelEvent.NB1_MALFUNCTION:
      return 65;

    case HyDryPanelEvent.FW_UPDATE_APPLY:
      return 68;
    case HyDryPanelEvent.FW_UPDATE_PERFORMED:
      return 69;
    
    case HyDryPanelEvent.POOR:
      return 71;
    case HyDryPanelEvent.PIR:
      return 72;
    case HyDryPanelEvent.POWER_THROTTLE_STEP:
      return 73;
    case HyDryPanelEvent.SUPPLY_VOLTAGE_FLUCTUATION:
      return 74;
    case HyDryPanelEvent.SUPPLY_VOLTAGE_STABLE:
      return 75;
    case HyDryPanelEvent.NB1_POWER_FAIL:
      return 76;

    case HyDryPanelEvent.CFG_REBOOT:
      return 80;
    case HyDryPanelEvent.FW_REBOOT:
      return 81;
    case HyDryPanelEvent.UTC_REBOOT:
      return 82;

    case HyDryPanelEvent.START_OF_DEPASSIVATION:
      return 91;
    case HyDryPanelEvent.END_OF_DEPASSIVATION:
      return 92;

    case HyDryPanelEvent.NOTE:
      return 1000;
    
    default:
      return 255;
  }
}

export function getHyDryPanelEventFromEventNumber(
  event: number
): HyDryPanelEvent {
  switch (event) {
    case 1:
      return HyDryPanelEvent.POWER_UP;
    case 2:
      return HyDryPanelEvent.POWER_FAIL;

    case 5:
      return HyDryPanelEvent.MANUAL_START;
    case 6:
      return HyDryPanelEvent.PROGRAM_START_DELAY;

    case 9:
      return HyDryPanelEvent.PROGRAM_START_FOLLOW_UP;
    case 10:
      return HyDryPanelEvent.PROGRAM_END_LAST_STEP;
    case 11:
      return HyDryPanelEvent.PROGRAM_END_MAX_DURATION;
    case 12:
      return HyDryPanelEvent.PROGRAM_END_HEAT_RATIO;
    case 13:
      return HyDryPanelEvent.MANUAL_STOP;
    case 14:
      return HyDryPanelEvent.BREAK_COOL;
    case 15:
      return HyDryPanelEvent.PROGRAM_CHAIN_MIN_DURATION;
    case 16:
      return HyDryPanelEvent.PROGRAM_CHAIN_MAX_DURATION;

    case 18:
      return HyDryPanelEvent.STEP;
    case 19:
      return HyDryPanelEvent.RESTART;
    case 20:
      return HyDryPanelEvent.MAX_HEAT_DURATION;
    case 21:
      return HyDryPanelEvent.CHANGE_OF_POSITION;
    case 22:
      return HyDryPanelEvent.MAX_SOIL_TEMP_REACHED;

    case 24:
      return HyDryPanelEvent.WALL_OVER_TEMP;
    case 25:
      return HyDryPanelEvent.PCB_OVER_TEMP;
    case 26:
      return HyDryPanelEvent.MAIN_CHIP_OVER_TEMP;
    case 27:
      return HyDryPanelEvent.EDGE_CHIP_OVER_TEMP;
    case 28:
      return HyDryPanelEvent.MAIN_PANEL_RE_ENABLED;
    case 29:
      return HyDryPanelEvent.EDGE_PANEL_RE_ENABLED;

    case 39:
      return HyDryPanelEvent.NB1_NO_NET;

    case 50:
      return HyDryPanelEvent.EEPROM_MALFUNCTION;
    case 51:
      return HyDryPanelEvent.INVALID_CONFIG;
    case 52:
      return HyDryPanelEvent.SPI_FLASH_MALFUNCTION;
    case 53:
      return HyDryPanelEvent.DATALOG_CORRUPTED;
    case 54:
      return HyDryPanelEvent.RTCC_MALFUNCTION;
    case 55:
      return HyDryPanelEvent.RTCC_INVALID_TIME;
    case 56:
      return HyDryPanelEvent.ADC_MALFUNCTION;
    case 57:
      return HyDryPanelEvent.IR_SENSOR_MALFUNCTION;

    case 59:
      return HyDryPanelEvent.EM_MALFUNCTION;
    case 60:
      return HyDryPanelEvent.SSR_MALFUNCTION;
    case 61:
      return HyDryPanelEvent.INVALID_PROGRAMS;

    case 63:
      return HyDryPanelEvent.ACC_MALFUNCTION;
    case 64:
      return HyDryPanelEvent.BLE_MALFUNCTION;
    case 65:
      return HyDryPanelEvent.NB1_MALFUNCTION;

    case 68:
      return HyDryPanelEvent.FW_UPDATE_APPLY;
    case 69:
      return HyDryPanelEvent.FW_UPDATE_PERFORMED;
    
    case 71:
      return HyDryPanelEvent.POOR;
    case 72:
      return HyDryPanelEvent.PIR;
    case 73:
      return HyDryPanelEvent.POWER_THROTTLE_STEP;
    case 74:
      return HyDryPanelEvent.SUPPLY_VOLTAGE_FLUCTUATION;
    case 75:
      return HyDryPanelEvent.SUPPLY_VOLTAGE_STABLE;
    case 76:
      return HyDryPanelEvent.NB1_POWER_FAIL;

    case 80:
      return HyDryPanelEvent.CFG_REBOOT;
    case 81:
      return HyDryPanelEvent.FW_REBOOT;
    case 82:
      return HyDryPanelEvent.UTC_REBOOT;

    case 91:
      return HyDryPanelEvent.START_OF_DEPASSIVATION;
    case 92:
      return HyDryPanelEvent.END_OF_DEPASSIVATION;
    
    case 1000:
      return HyDryPanelEvent.NOTE;

    default:
      return HyDryPanelEvent.UNKNOWN;
  }
}

function decodeMessage(
  serialNumber: string,
  eventCategory: HyDryEventCategory,
  data: string
): HyDryEventMessage {
  const tokens = data.split(":");
  const dataBase64 = tokens[0];
  const additionalData = tokens.slice(1).join(":");

  const eventInfoData = new Uint8Array(base64Buffer.decode(dataBase64));

  const wasRead = eventInfoData[0] % 2 !== 0;
  const eventNumber = eventInfoData[0] >> 1;

  const hyDryEvent = getHyDryEventFromEventNumber(eventNumber);
  const timestampMinutes = getUint32(eventInfoData, 1);

  const date = new Date(60 * 1000 * timestampMinutes);

  return {
    additionalData: tokens.length > 0 ? additionalData : undefined,
    serialNumber: serialNumber,
    eventCategory: eventCategory,
    event: hyDryEvent,
    eventNumber: eventNumber,
    messageDate: date,
    wasRead: wasRead,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parsePageData(pageData: any, result: LatestHyDryEventMessageInfo[]) {
  if (!pageData) {
    return;
  }

  for (const serialNumber of Object.keys(pageData)) {
    const deviceEvents = pageData[serialNumber];
    const unreadInfo = deviceEvents["unread"];
    const messages: UnreadHyDryEventMessagesInfo[] = [];

    for (const eventCategory of Object.keys(deviceEvents)) {
      if (eventCategory === "unread") {
        continue;
      }

      const data = deviceEvents[eventCategory];

      const category = Number(eventCategory) as HyDryEventCategory;
      const message: HyDryEventMessage = decodeMessage(
        serialNumber,
        category,
        data
      );

      const numUnread = unreadInfo
        ? unreadInfo[eventCategory] ?? undefined
        : undefined;

      if (!message.wasRead && numUnread !== undefined) {
        messages.push({
          numUnread: numUnread,
          latestMessage: message,
          eventCategory: category,
        });
      }
    }

    if (messages.length > 0) {
      result.push({
        serialNumber,
        unreadMessages: messages.sort((a, b) =>
          compareHyDryEventMessages(a.latestMessage, b.latestMessage)
        ),
      });
    }
  }
}

export function getHyDryEventMessagesFromPage(
  hyDryEventPage: HyDryEventsPage
): LatestHyDryEventMessageInfo[] {
  const result: LatestHyDryEventMessageInfo[] = [];

  parsePageData(hyDryEventPage.comBoxes, result);
  parsePageData(hyDryEventPage.panels, result);

  return result;
}

export function getHyDryEventCategoryDescription(
  vue: Vue,
  hyDryEventCategory: HyDryEventCategory,
  useLongDescription?: boolean,
  eventNumber?: number
): string {
  const resPrefix = `hyDryEvents.categories.${useLongDescription ? "longDescription" : "shortDescription"
    }`;

  switch (hyDryEventCategory) {
    case HyDryEventCategory.NOTE:
      return vue.$t(`${resPrefix}.note`).toString();
    case HyDryEventCategory.LID:
      return vue.$t(`${resPrefix}.lid`).toString();
    case HyDryEventCategory.PAUSE:
      return vue.$t(`${resPrefix}.pause`).toString();
    case HyDryEventCategory.SENSOR:
      if (eventNumber && isEventNumberOfWarningOrError(eventNumber)) {
        return vue.$t(`${resPrefix}.alert`).toString();
      }

      return vue.$t(`${resPrefix}.sensor`).toString();
    case HyDryEventCategory.IRES:
      return vue.$t(`${resPrefix}.ires`).toString();
    case HyDryEventCategory.ORGANISATION:
      return vue.$t(`${resPrefix}.organisation`).toString();
    case HyDryEventCategory.DRYING_PROGRESS:
      return vue.$t(`${resPrefix}.drying`).toString();
    case HyDryEventCategory.ALERT:
      return vue.$t(`${resPrefix}.alert`).toString();
    case HyDryEventCategory.OTHER:
      return vue.$t(`${resPrefix}.other`).toString();
    case HyDryEventCategory.NOT_IN_MESSAGE:
      return "";
  }
}

const LID_EVENTS = new Set([
  getHyDryEventNumber(HyDryEvent.LID_OPENED),
  getHyDryEventNumber(HyDryEvent.LID_CLOSED),
]);

const SENSOR_EVENTS = new Set([
  getHyDryEventNumber(HyDryEvent.SENSOR_CONFIGURATION_CHANGED)
]);

const PAUSE_EVENTS = new Set([
  getHyDryEventNumber(HyDryEvent.TIMER_PAUSE_STARTED),
  getHyDryEventNumber(HyDryEvent.TIMER_PAUSE_ENDED),

  getHyDryEventNumber(HyDryEvent.MANUAL_PAUSE_STARTED),
  getHyDryEventNumber(HyDryEvent.MANUAL_PAUSE_ENDED),

  getHyDryEventNumber(HyDryEvent.PROGRAM_PAUSE_STARTED),
  getHyDryEventNumber(HyDryEvent.PROGRAM_PAUSE_ENDED),
]);

const EVENTS_FOR_IRES_MEMBERS = new Set([
  getHyDryEventNumber(HyDryEvent.DEVICE_OVERDUE)
]);

/**
 * ComBox log events a client may not see, decided in the "HyDry device events: client visibility
 * review". These are the four `Fehler:` / `Warnung:` logs, whose text is a list of raw ComBox error
 * flags: device-internal diagnostics, not something a client can act on.
 *
 * They are hidden only while the log is still categorised as `ALERT`. A warning whose flags are
 * 11-15 is re-labelled `SENSOR` by `HyDryLogsRepository.convertSnapshots`, and those stay available
 * to a client behind the SENSOR filter button.
 */
const COMBOX_LOG_EVENTS_HIDDEN_FROM_CLIENTS = new Set([
  getHyDryEventNumber(HyDryEvent.MALFUNCTION),
  getHyDryEventNumber(HyDryEvent.MALFUNCTION_WHILE_DRYING),
  getHyDryEventNumber(HyDryEvent.WARNING),
  getHyDryEventNumber(HyDryEvent.WARNING_WHILE_DRYING),
]);

/**
 * Panel log events a client may not see, decided in the same review: the hardware fault block
 * (EEPROM, device configuration, SPI flash, datalog, RTCC, ADC, program table, BLE and NB1 module).
 *
 * Panel events 57, 59, 60 and 63 (IR sensor, energy meter, power usage, position sensor) were
 * reviewed and deliberately left client-visible, so they are not listed here.
 *
 * These carry the `ALERT` category, which is why the category filter alone cannot express this.
 */
const PANEL_LOG_EVENTS_HIDDEN_FROM_CLIENTS = new Set([
  50, 51, 52, 53, 54, 55, 56, 61, 64, 65,
]);

/**
 * ComBox **device** events (vocabulary A) a client may see as "Letztes Ereignis": 47 of the 71
 * translated numbers, from the "HyDry device events: client visibility review".
 *
 * **The Cloud owns this rule.** `COMBOX_EVENTS_VISIBLE_TO_CLIENTS` in
 * `functions/src/data/models/HyDryEventInfo.ts` is what writes `comBoxStatus.lastClientEvent`, and
 * that field is what a client reads. This is a mirror, used *only* as the fallback for a status
 * document written before the Cloud began writing the field (2026-08-27): measured that day, 2527
 * of 3784 production ComBoxes still carried a client-safe `lastEvent` and no `lastClientEvent`, and
 * with no fallback every one of them showed a client an empty field until the box next reported.
 *
 * Keep the two lists identical. Being a mirror it can drift, which is why nothing else reads it: a
 * document that has the field always wins over this list.
 *
 * An allow-list rather than a deny-list on purpose: the box reports far more numbers than are
 * translated, and an unknown one used to reach the client as a bare integer.
 */
const COMBOX_DEVICE_EVENTS_VISIBLE_TO_CLIENTS = new Set([
  1, 2, 3, 6, 7, 9, 10, 11, 12, 13,
  14, 16, 17, 18, 20, 21, 23, 24, 25, 26,
  28, 30, 31, 34, 36, 37, 44, 56, 59, 60,
  61, 62, 64, 65, 66, 67, 68, 69, 77, 80,
  81, 82, 83, 84, 86, 88, 125,
]);

/**
 * ComBox error flags that mean "a sensor has a problem" rather than "the box has a problem". A
 * warning or malfunction carrying one of these is displayed as a SENSOR event.
 */
const SENSOR_ERROR_FLAGS = [11, 12, 13, 14, 15];

export function isEventNumberOfHyDryLidEvent(eventNumber: number) {
  return LID_EVENTS.has(eventNumber);
}

export function isEventNumberOfSensorEvent(eventNumber: number) {
  return SENSOR_EVENTS.has(eventNumber);
}

export function isEventNumberOfIresMembersEvent(eventNumber: number) {
  return EVENTS_FOR_IRES_MEMBERS.has(eventNumber);
}

/**
 * Whether a ComBox **device** event number (vocabulary A) may be shown to a client.
 *
 * Only for the `lastClientEvent` fallback described on
 * {@link COMBOX_DEVICE_EVENTS_VISIBLE_TO_CLIENTS}. The Logbuch has its own rule
 * ({@link isHyDryEventLogAllowedByCapabilities}) over a different vocabulary; do not mix them.
 */
export function isComBoxDeviceEventVisibleToClients(eventNumber: number) {
  return COMBOX_DEVICE_EVENTS_VISIBLE_TO_CLIENTS.has(eventNumber);
}

/**
 * The ComBox device event a **client** may see as "Letztes Ereignis".
 *
 * `lastClientEvent` is the answer whenever the Cloud has written one, because the Cloud owns the
 * rule. It only writes it when the box reports, and it began doing so on 2026-08-27: measured that
 * day, 2527 of 3784 production ComBoxes carried a client-safe `lastEvent` and no `lastClientEvent`.
 * Reading the new field alone would therefore have blanked the row for two thirds of the fleet until
 * each box next reported, and a box in storage can be months away from that.
 *
 * So an absent field falls back to the raw event, re-applying the Cloud's own allow-list, which
 * means the fallback can only ever surface an event the visibility review cleared for clients.
 *
 * Takes the status fields rather than a `ComBoxStatus`, because `ComBox` imports this module.
 *
 * @returns the event number to render, or `undefined` when a client may see nothing.
 */
export function resolveClientVisibleComBoxEvent(status: {
  lastEvent?: number | null;
  lastClientEvent?: number | null;
}): number | undefined {
  if (status.lastClientEvent !== undefined && status.lastClientEvent !== null) {
    return status.lastClientEvent;
  }

  const lastEvent = status.lastEvent;

  return lastEvent !== undefined &&
    lastEvent !== null &&
    isComBoxDeviceEventVisibleToClients(lastEvent)
    ? lastEvent
    : undefined;
}

export function isEventNumberOfHyDryPauseEvent(eventNumber: number) {
  return PAUSE_EVENTS.has(eventNumber);
}

/**
 * The category a log is actually displayed and filtered under.
 *
 * The Cloud stores lid and pause events as ORGANISATION and every warning as ALERT; Office splits
 * them out so the Logbuch can offer LID / PAUSE / SENSOR buttons. Both the log list and the message
 * list must agree on this, so the rewrite lives here and not in a repository.
 *
 * @param additionalData the log's `additionalData`; for a warning or malfunction it is the
 *   comma-separated ComBox error flags, and flags 11-15 mean the fault is a sensor problem.
 * @param isPanel a Panel log stores a RAW Panel event number, which collides with the ComBox log
 *   vocabulary (Panel 60 is a power-usage fault, ComBox 60 is `LID_CLOSED`). Panel categories are
 *   already final as the Cloud wrote them, so nothing is rewritten for a Panel.
 */
export function resolveEffectiveHyDryEventCategory(
  eventNumber: number,
  eventCategory: HyDryEventCategory,
  additionalData?: string,
  isPanel?: boolean
): HyDryEventCategory {
  if (isPanel) {
    return eventCategory;
  }

  if (isEventNumberOfHyDryLidEvent(eventNumber)) {
    return HyDryEventCategory.LID;
  }

  if (isEventNumberOfHyDryPauseEvent(eventNumber)) {
    return HyDryEventCategory.PAUSE;
  }

  if (isEventNumberOfSensorEvent(eventNumber)) {
    return HyDryEventCategory.SENSOR;
  }

  // ==================== FIRMWARE v4.0.0+ CHANGES ====================
  if (isEventNumberOfWarningOrError(eventNumber) && additionalData) {
    const errorFlags = additionalData
      .split(",")
      .map((value) => parseInt(value))
      .filter((flag) => !isNaN(flag));

    if (SENSOR_ERROR_FLAGS.some((flag) => errorFlags.includes(flag))) {
      return HyDryEventCategory.SENSOR;
    }
  }

  return eventCategory;
}

/**
 * Logbuch categories that start switched OFF.
 *
 * SENSOR is off for everyone, IRES included: the firmware-v4 sensor edges are chatty enough to bury
 * the drying events, and the filter button is always offered so anyone can switch them back on. This
 * is a default, not a visibility rule.
 */
const HY_DRY_EVENT_CATEGORIES_OFF_BY_DEFAULT = [HyDryEventCategory.SENSOR];

/**
 * The categories switched on when the Logbuch opens.
 *
 * Returns a new array, so the caller cannot accidentally mutate the master list it passed in.
 */
export function getDefaultSelectedHyDryEventCategories(
  allCategories: HyDryEventCategory[]
): HyDryEventCategory[] {
  return allCategories.filter(
    (category) => !HY_DRY_EVENT_CATEGORIES_OFF_BY_DEFAULT.includes(category)
  );
}

/**
 * The capabilities that decide whether a HyDry event log may be shown at all.
 * Both are IRES-only, see `src/permissions/UserPermissions.ts`.
 */
export interface HyDryEventLogAccess {
  /** `SEE_ALL_EVENT_LOGS` - logs before 2023 and IRES-internal events. */
  canSeeAllEventLogs: boolean;
  /** `SEE_LID_AND_PAUSE_EVENT_LOGS` - lid openings and drying pauses. */
  canSeeLidAndPauseEventLogs: boolean;
}

/** The log being checked, as far as visibility is concerned. */
export interface HyDryEventLogVisibilityInput {
  eventNumber: number;
  /**
   * The *effective* category, i.e. after `HyDryLogsRepository.convertSnapshots` rewrote
   * ORGANISATION to LID / PAUSE and an error-flag warning to SENSOR. The hide rules below depend on
   * that rewrite, so never pass the raw category straight off the Firestore document.
   */
  eventCategory: HyDryEventCategory;
  /** Panels and ComBoxes number their log events differently, so the rules differ. */
  isPanel: boolean;
}

/**
 * Whether the user's capabilities permit this event log at all.
 *
 * This is the access gate only; it runs before the category toggle buttons and
 * is independent of them. A missing capability hides the log no matter which
 * filters the user has switched on.
 *
 * The two capabilities are checked independently. On a ComBox the lid/pause rule is the only one
 * that can apply to a holder of `SEE_ALL_EVENT_LOGS`; every other rule here is a client rule.
 */
export function isHyDryEventLogAllowedByCapabilities(
  log: HyDryEventLogVisibilityInput,
  access: HyDryEventLogAccess
): boolean {
  if (log.isPanel) {
    /* Everything below is a client rule, and the ComBox event sets do not apply to a Panel: the two
       vocabularies share numbers with completely different meanings. */
    if (access.canSeeAllEventLogs) {
      return true;
    }

    return !PANEL_LOG_EVENTS_HIDDEN_FROM_CLIENTS.has(log.eventNumber);
  }

  if (
    (isEventNumberOfHyDryLidEvent(log.eventNumber) ||
      isEventNumberOfHyDryPauseEvent(log.eventNumber)) &&
    !access.canSeeLidAndPauseEventLogs
  ) {
    return false;
  }

  if (access.canSeeAllEventLogs) {
    return true;
  }

  if (isEventNumberOfIresMembersEvent(log.eventNumber)) {
    return false;
  }

  if (COMBOX_LOG_EVENTS_HIDDEN_FROM_CLIENTS.has(log.eventNumber)) {
    // A sensor problem stays visible; a device fault does not.
    return log.eventCategory === HyDryEventCategory.SENSOR;
  }

  return true;
}

export function getHyDryEventDescription(
  vue: Vue,
  hyDryEvent: HyDryEvent,
  additionalInfo?: string,
  useLongDescription?: boolean,
  step?: number,
  doesSupportExtendedSensors?: boolean,
) {
  let res = getHyDryEventDescriptionStringRes(hyDryEvent);

  if (hyDryEvent == HyDryEvent.PROLONGED_POWER_FAIL_WHILE_DRYING) {
    if (additionalInfo) {
      res = "formatPowerFailWhileDrying";
    }
  }

  if (hyDryEvent == HyDryEvent.PROGRAM_PHASE_ENDED) {
    const stepFromAdditionalInfo = Number(additionalInfo);

    if (step !== undefined) {
      additionalInfo = (step+1).toString();
    }
    else if (stepFromAdditionalInfo || stepFromAdditionalInfo === 0) {
      additionalInfo = (stepFromAdditionalInfo+1).toString();
    }
  }

  if (
    hyDryEvent == HyDryEvent.WARNING ||
    hyDryEvent == HyDryEvent.MALFUNCTION ||
    hyDryEvent == HyDryEvent.WARNING_WHILE_DRYING ||
    hyDryEvent == HyDryEvent.MALFUNCTION_WHILE_DRYING
  ) {
    const errorFlags: number[] = [];

    additionalInfo?.split(",").forEach((value) => {
      const errorFlag = parseInt(value);

      if (!isNaN(errorFlag)) {
        errorFlags.push(errorFlag);
      }
    });

    return vue.$t(
      `hyDryEvents.${useLongDescription ? "longDescription" : "shortDescription"
      }.${res}`,
      // ==================== FIRMWARE v4.0.0+ CHANGES ====================
      [getComBoxErrorFlagDescriptions(vue, errorFlags, { doesSupportExtendedSensors })]
    );
  }

  if (hyDryEvent === HyDryEvent.SENSOR_CONFIGURATION_CHANGED) {
    const scope = `hyDryEvents.${useLongDescription ? "longDescription" : "shortDescription"}`;

    if (!additionalInfo) {
      return vue.$t(`${scope}.${res}`).toString();
    }

    const [typeStr, slotStr] = additionalInfo.split(";");
    const type = Number(typeStr);
    const slot = slotStr !== undefined ? Number(slotStr) : undefined;

    // ==================== FIRMWARE v4.0.0+ CHANGES ====================
    switch (type) {
      case 1: // battery weak (bit 0 rise)
        return vue.$t(`${scope}.sensorBatteryWeak`, [slot]).toString();
      case 2: // link weak (bit 2 rise)
        return vue.$t(`${scope}.sensorLinkWeak`, [slot]).toString();
      case 3: // link weak (bit 2 fall)
        return vue.$t(`${scope}.sensorLinkGood`, [slot]).toString();
      case 4: // back in range (bit 4 fall)
        return vue.$t(`${scope}.sensorBackInRange`, [slot]).toString();
      case 5: // link timeout (bit 5 rise)
        return vue.$t(`${scope}.sensorLinkTimeout`, [slot]).toString();
      case 6: // link timeout resolved (bit 5 fall)
        return vue.$t(`${scope}.sensorLinkTimeoutResolved`, [slot]).toString();
      case 7: // button pressed (bit 6 rise)
        return vue.$t(`${scope}.sensorButtonPressed`, [slot]).toString();
      case 8: // turned off (bit 7 rise)
        return vue.$t(`${scope}.sensorTurnedOff`, [slot]).toString();
      
      case 10: { // snapshot at dry run start
        // Format: 10;event;serial,slot,fw,batt,rssi|...
        const splitInfo = additionalInfo.split(";");
        const eventNo = splitInfo[1];
        const rows = (splitInfo[2] || "")
          .split("|")
          .filter(Boolean);

        const items = rows.map(row => {
          const [serialNumber, slotStr, fwStr, batteryStr, rssi] = row.split(",");
          const parts: string[] = [];
          if (fwStr) parts.push(vue.$t(`${scope}.firmware`, [fwStr]).toString());
          if (batteryStr) parts.push(vue.$t(`${scope}.battery`, [batteryStr]).toString());
          if (rssi) parts.push(vue.$t(`${scope}.rssi`, [rssi]).toString());
          return `${serialNumber} (Slot ${slotStr}${parts.length ? ", " + parts.join(", ") : ""})`;
        });

        return vue
          .$t(`${scope}.sensorConfigurationChangedWithEventNo`, [
            eventNo,
            items.join("\n"),
          ])
          .toString();
      }
      default:
        // Unknown subtype → fall back to generic with raw info
        return vue.$t(`${scope}.${res}`, [additionalInfo]).toString();
    }
  }

  return vue.$t(
    `hyDryEvents.${useLongDescription ? "longDescription" : "shortDescription"
    }.${res}`,
    [additionalInfo]
  );
}

const EVENTS_WITH_DIFFERENT_LOG_INFO = [
  getHyDryPanelEventNumber(HyDryPanelEvent.POWER_UP),
  getHyDryPanelEventNumber(HyDryPanelEvent.POWER_FAIL),
  getHyDryPanelEventNumber(HyDryPanelEvent.MANUAL_START),
  getHyDryPanelEventNumber(HyDryPanelEvent.PROGRAM_START_DELAY),
  getHyDryPanelEventNumber(HyDryPanelEvent.MANUAL_STOP),
  getHyDryPanelEventNumber(HyDryPanelEvent.WALL_OVER_TEMP),
  getHyDryPanelEventNumber(HyDryPanelEvent.PCB_OVER_TEMP),
  getHyDryPanelEventNumber(HyDryPanelEvent.MAIN_PANEL_RE_ENABLED),
  getHyDryPanelEventNumber(HyDryPanelEvent.EDGE_PANEL_RE_ENABLED),
  getHyDryPanelEventNumber(HyDryPanelEvent.NB1_NO_NET),
  getHyDryPanelEventNumber(HyDryPanelEvent.POOR),
  getHyDryPanelEventNumber(HyDryPanelEvent.PIR),
];

export function getHyDryPanelEventDescription(
  vue: Vue,
  hyDryPanelEvent: HyDryPanelEvent,
  iresDevice?: IresDevice
) {
  const lastClientEventNumber = getHyDryPanelEventNumber(hyDryPanelEvent);

  if (lastClientEventNumber === getHyDryPanelEventNumber(HyDryPanelEvent.MAX_HEAT_DURATION)) {
    if (iresDevice && isInWarning(iresDevice)) {
      return vue.$t(
        `panel.events.event${lastClientEventNumber}Warning`
      );
    }
  }
  else if (EVENTS_WITH_DIFFERENT_LOG_INFO.includes(lastClientEventNumber)) {
    return vue.$t(
      `panel.events.event${lastClientEventNumber}Log`
    );
  }

  return vue.$t(
    `panel.events.event${lastClientEventNumber}`
  );
}

export function getHyDryEventMessageDescription(
  vue: Vue,
  hyDryEventMessage: HyDryEventMessage,
  isPanel: boolean,
  useLongDescription?: boolean,
  doesSupportExtendedSensors?: boolean,
) {
  if (isPanel) {
    return getHyDryPanelEventDescription(
      vue,
      getHyDryPanelEventFromEventNumber(hyDryEventMessage.eventNumber),
    );
  }

  return getHyDryEventDescription(
    vue,
    getHyDryEventFromEventNumber(hyDryEventMessage.eventNumber),
    hyDryEventMessage.additionalData,
    useLongDescription,
    undefined,
    doesSupportExtendedSensors
  );
}
